import _extends from '@babel/runtime/helpers/extends';
import React, { useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { C as ConditionalFocusTrap } from './ConditionalFocusTrap-186900b2.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';

/**
Component to display an Overlay
*/
function Overlay({
  className,
  onDismiss,
  children,
  status,
  componentRef,
  renderNode,
  focusTrap,
  ...otherProps
}) {
  const cssClasses = ['st_overlay', `st_overlay--${status}`];
  let OverlayClasses = ['st_overlay__content'];
  OverlayClasses = addClassNames(className, OverlayClasses);
  const contentRef = useRef(null);

  // handle when X is clicked to manually close Overlay
  const handleDismiss = useCallback(() => {
    if (onDismiss) onDismiss();
  }, [onDismiss]);

  // close when user clicks on scrim
  const listenClick = useCallback(e => {
    if (contentRef.current && !contentRef.current.contains(e.target)) {
      handleDismiss();
    }
  }, [handleDismiss, contentRef]);
  const listenKeyboard = useCallback(e => {
    if (e.key === 'Escape' || e.keyCode === 27) {
      handleDismiss();
    }
  }, [handleDismiss]);

  // listen for Esc key
  useEffect(() => {
    window.addEventListener('keydown', listenKeyboard);
    return () => window.removeEventListener('keydown', listenKeyboard);
  }, [listenKeyboard]);

  /* eslint-disable */
  // need to disable some a11y rules around clicking the scrim to close modal
  // the key event is attached to window already
  // eslint-disable jsx-a11y/click-events-have-key-events jsx-a11y/no-static-element-interactions
  const content = /*#__PURE__*/React.createElement(ConditionalFocusTrap, {
    focusTrap: focusTrap,
    focusTrapOptions: {
      fallbackFocus: `.${OverlayClasses.join('.')}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: cssClasses.join(' '),
    ref: componentRef,
    onClick: listenClick,
    "aria-label": "Press Esc to Close"
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: OverlayClasses.join(' ')
  }, otherProps, {
    role: "dialog",
    ref: contentRef,
    tabIndex: -1
  }), children)));
  /* eslint-enable */

  if (status === 'initial' || status === 'closed') return null;
  if (!renderNode) return content;
  return /*#__PURE__*/ReactDOM.createPortal(content, renderNode);
}
Overlay.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** callback when Overlay is dismissed, either via the X icon, or by pressing Esc */
  onDismiss: PropTypes.func,
  /** node where Overlay should be rendered */
  renderNode: PropTypes.any,
  /** status of Overlay, generally should be open or closed */
  status: PropTypes.oneOf(['initial', 'beforeopen', 'opening', 'closing', 'closed', 'open']),
  /** ref for element */
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  /** whether to trap focus when Dialog is open, for accessibility */
  focusTrap: PropTypes.bool
};
Overlay.defaultProps = {
  className: null,
  onDismiss: () => null,
  renderNode: null,
  status: 'initial',
  componentRef: null,
  focusTrap: true
};
Overlay.displayName = 'Overlay';

export { Overlay as O };
